import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../features/auth/useAuth'
import { ApkDownloadLink } from '../features/install/ApkDownloadLink'
import { AppMark } from '../shared/components/icons'
import { BUILD_TARGET } from '../shared/config'
import { czechPlural } from '../shared/czech'
import { useOnline, useSyncCounts } from '../shared/hooks'
import styles from './AppShell.module.css'
import { paths } from './routes'

/**
 * App shell and navigation, cleanly separated from the task renderer.
 * Nothing in here reaches into `@sciolingo/renderer`, which is what keeps a
 * future native rewrite of the shell from dragging the renderer with it.
 */
export function AppShell() {
  const { logout } = useAuth()
  const online = useOnline()
  const counts = useSyncCounts()
  const location = useLocation()

  const waiting = counts.pending + counts.inflight + counts.failed

  return (
    <div className={styles.shell}>
      <header className={styles.bar}>
        <Link className={styles.brand} to={paths.home}>
          <AppMark className={styles.mark} />
          <span>Sciolingo</span>
        </Link>

        <div className={styles.status}>
          {/* §10.5: an offline indicator whenever the network is unavailable. */}
          {!online ? (
            <span className={`pill ${styles.offline}`} role="status">
              Offline
            </span>
          ) : null}

          {/*
            §10.5: the persistent pending-sync counter. It stays visible until the
            queue drains because it is the only available mitigation for the
            absence of Background Sync on iOS — a student who closes the app
            with answers still queued may never send them, and this is the one
            chance to tell them so.
          */}
          {waiting > 0 ? (
            <span className={`pill ${styles.pending}`} role="status">
              {waiting} {czechPlural(waiting, ['odpověď', 'odpovědi', 'odpovědí'])} čeká na odeslání
              {counts.failed > 0 ? ` · ${counts.failed} chyb` : ''}
            </span>
          ) : null}

          <Link className={styles.link} to={paths.home} aria-current={location.pathname === paths.home}>
            Home
          </Link>

          <Link
            className={styles.link}
            to={paths.diagnostics}
            aria-current={location.pathname === paths.diagnostics}
          >
            Diagnostika
          </Link>

          <ApkDownloadLink className={styles.link} />

          <button className={`${styles.link} ${styles.linkButton}`} type="button" onClick={() => void logout()}>
            Login
          </button>
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <footer className={styles.foot}>
        <span>
          Build <code>{BUILD_TARGET}</code>
        </span>
        <ApkDownloadLink />
      </footer>
    </div>
  )
}
